import { createReducer } from '@reduxjs/toolkit'
import { types } from 'actions/condition'

const SIBLINGS = {
  right: 'left',
  left: 'right',
}

const DEFAULT_FIELDS = {
  group: { type: 'group' },
  condition: {
    type: 'condition',
    condition: { table: '' },
  },
}

// Helper function to get value at path in nested object
function getIn(obj, path) {
  return path.reduce((current, key) => current?.[key], obj)
}

// Helper function to set value at path in nested object (using Immer)
function setIn(draft, path, value) {
  if (path.length === 0) {
    return value
  }

  let current = draft
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i]
    if (!current[key]) {
      current[key] = {}
    }
    current = current[key]
  }

  current[path[path.length - 1]] = value
}

// Helper function to delete value at path in nested object (using Immer)
function deleteIn(draft, path) {
  if (path.length === 0) return

  const parentPath = path.slice(0, -1)
  const lastKey = path[path.length - 1]

  if (parentPath.length === 0) {
    delete draft[lastKey]
  } else {
    const parent = getIn(draft, parentPath)
    if (parent) {
      delete parent[lastKey]
    }
  }
}

// Helper function to merge values at path
function mergeIn(draft, path, attributes) {
  const target = path.length === 0 ? draft : getIn(draft, path)
  if (target && typeof target === 'object') {
    Object.assign(target, attributes)
  }
}

export default function reducer(state = {}, action, defaultFields = DEFAULT_FIELDS) {
  const path = action.path ? (Array.isArray(action.path) ? action.path : action.path.toArray()) : []

  return createReducer(state, (builder) => {
    builder
      .addCase(types.REMOVE_CONDITION_TYPE, (draft) => {
        if (path.length === 0) return

        const last = path[path.length - 1]
        const siblingKey = SIBLINGS[last]

        if (siblingKey) {
          const parentPath = path.slice(0, -1)
          const sibling = getIn(draft, [...parentPath, siblingKey])
          setIn(draft, parentPath, sibling)
        } else {
          deleteIn(draft, path)
        }
      })
      .addCase(types.ADD_CONDITION_TYPE, (draft) => {
        const contentPath = [...path, 'content']
        const currentContent = getIn(draft, contentPath)

        let newValue = null
        if (currentContent) {
          newValue = {
            type: 'and',
            left: currentContent,
            right: defaultFields[action.conditionType],
          }
        } else {
          newValue = defaultFields[action.conditionType]
        }

        setIn(draft, contentPath, newValue)
      })
      .addCase(types.UPDATE_CONDITION_TYPE, (draft) => {
        mergeIn(draft, path, action.attributes)
      })
  })(state, action)
}
